import { Notification } from 'types/notification';

// ─── Date Grouping ──────────────────────────────────────────────────────────

export interface GroupedNotifications {
  today: Notification[];
  yesterday: Notification[];
  earlier: Notification[];
}

/**
 * Groups notifications by date relative to the current date.
 *
 * - "Today"     → createdAt date matches the current calendar date
 * - "Yesterday" → createdAt date matches the previous calendar date
 * - "Earlier"   → everything else
 *
 * Each bucket preserves the original array order (assumed reverse-chronological).
 */
export function groupNotificationsByDate(
  notifications: Notification[],
): GroupedNotifications {
  const now = new Date();
  const todayStr = toDateString(now);

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = toDateString(yesterday);

  const grouped: GroupedNotifications = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  for (const notification of notifications) {
    const dateStr = toDateString(new Date(notification.createdAt));

    if (dateStr === todayStr) {
      grouped.today.push(notification);
    } else if (dateStr === yesterdayStr) {
      grouped.yesterday.push(notification);
    } else {
      grouped.earlier.push(notification);
    }
  }

  return grouped;
}

/** Returns a locale-independent YYYY-MM-DD string for date comparison. */
function toDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// ─── Badge Formatting ───────────────────────────────────────────────────────

/**
 * Formats an unread count for display in a badge.
 *
 * - Returns the count as a string when 1–99
 * - Returns "99+" when count exceeds 99
 * - Returns an empty string when count is 0 (badge should be hidden)
 */
export function formatBadgeCount(count: number): string {
  if (!count || count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

// ─── Accessibility ──────────────────────────────────────────────────────────

/**
 * Returns an accessible aria-label string for the notification badge.
 *
 * Examples:
 *   getBadgeAriaLabel(5)  → "5 unread notifications"
 *   getBadgeAriaLabel(1)  → "1 unread notification"
 *   getBadgeAriaLabel(0)  → ""
 */
export function getBadgeAriaLabel(count: number): string {
  if (!count || count <= 0) return '';
  const noun = count === 1 ? 'notification' : 'notifications';
  return `${count} unread ${noun}`;
}
